import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

const reviewHref = (d) =>
  d.has_analogy === false && d.subject_id ? `/app/subjects/${d.subject_id}` : `/app/topic/${d.topic_id}`;
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Calendar, RefreshCw, Loader2, CheckCircle2, ArrowRight, Sparkles, BookOpenCheck } from "lucide-react";
import CoachToolModal from "@/components/CoachToolModal";

const ACTIVITY_ICON = {
  flashcard: "🎴",
  questions: "🧠",
  review: "📖",
};

export default function StudyPlan() {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [aiTool, setAiTool] = useState(null); // { type, defaults }

  const load = async () => {
    try {
      const r = await api.get("/study-plan/current");
      // Legacy cached plans (built before the one-subject-per-weekday
      // rotation) don't carry `subject_label` on any day. Auto-regenerate
      // once so existing users see the new plan without hitting Regenerate.
      const isLegacy = !r.data?.rotation_note ||
        !r.data?.days?.some((d) => d.subject_label);
      if (isLegacy) {
        const fresh = await api.post("/study-plan/generate");
        setPlan(fresh.data);
      } else {
        setPlan(r.data);
      }
    } catch (e) {
      toast.error("Couldn't load your plan.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const regenerate = async (difficulty = null) => {
    setRegenerating(true);
    try {
      const params = difficulty ? { difficulty } : {};
      const r = await api.post("/study-plan/generate", null, { params });
      setPlan(r.data);
      const label = { easy: "Easy", medium: "Balanced", hard: "Hard" }[r.data.difficulty] || "";
      toast.success(label ? `Plan refreshed — ${label} difficulty.` : "Plan refreshed for this week.");
    } catch (e) {
      toast.error("Regeneration failed.");
    } finally {
      setRegenerating(false);
    }
  };

  const completeDay = async (date) => {
    try {
      await api.post("/study-plan/complete-day", null, { params: { date } });
      setPlan((p) => ({
        ...p,
        days: p.days.map((d) => (d.date === date ? { ...d, completed: true } : d)),
      }));
      toast.success("Nice — day marked done. 🎯");
    } catch (e) {
      toast.error("Couldn't mark done.");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-16 justify-center" data-testid="plan-loading">
        <Loader2 className="animate-spin" size={18} /> Building your plan…
      </div>
    );
  }
  if (!plan) return null;

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="max-w-5xl mx-auto" data-testid="study-plan-page">
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="pm-badge mb-2" style={{ background: "var(--hl-green)", color: "var(--primary)" }}>
            <Calendar size={12} className="mr-1.5" /> Week of {plan.week_start}
          </div>
          <h1 className="font-display text-4xl font-bold" data-testid="plan-heading">Your Weekly Study Plan</h1>
          <p className="mt-2 text-lg max-w-2xl" style={{ color: "var(--text-secondary)" }}>
            One MCAT subject per weekday — Biology, Biochem, Gen Chem, Orgo, Physics, Psych, Sociology — with your weakest topic in each auto-picked for the day.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setAiTool({
              type: "exam",
              defaults: {
                subject: "biology",
                topics: "",
                days: 7,
                hours_per_day: 2,
              },
            })}
            className="pm-btn-primary inline-flex items-center gap-2"
            data-testid="plan-ai-exam-btn"
            title="Build a personalized AI exam plan — Pro (0.25 credit)"
          >
            <BookOpenCheck size={14} /> AI Exam Plan
          </button>
          <button
            onClick={() => regenerate()}
            disabled={regenerating}
            className="pm-btn-secondary inline-flex items-center gap-2"
            data-testid="plan-regenerate-btn"
          >
            {regenerating ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            Regenerate now
          </button>
        </div>
      </div>

      {/* Difficulty picker — controls how the plan ranks candidates in each subject */}
      <div className="pm-card p-3 mb-5 flex items-center gap-3 flex-wrap" data-testid="plan-difficulty-picker">
        <span className="text-xs uppercase tracking-wider font-semibold" style={{ color: "var(--text-muted)" }}>Difficulty:</span>
        {[
          { id: "easy", label: "Easy", desc: "Confidence builders — topics you're strong on" },
          { id: "medium", label: "Balanced", desc: "Weak spots below 80%, then fresh terrain" },
          { id: "hard", label: "Hard", desc: "Tackle your absolute weakest topics head-on" },
        ].map((d) => {
          const active = plan.difficulty === d.id;
          return (
            <button
              key={d.id}
              onClick={() => regenerate(d.id)}
              disabled={regenerating}
              className="pm-badge inline-flex flex-col items-start gap-0"
              style={{
                background: active ? "var(--primary)" : "var(--muted)",
                color: active ? "white" : "var(--text)",
                cursor: "pointer",
                padding: "6px 14px",
                textAlign: "left",
              }}
              data-testid={`plan-difficulty-${d.id}`}
              title={d.desc}
            >
              <span className="text-sm font-bold">{d.label}</span>
              <span className="text-xs opacity-80">{d.desc}</span>
            </button>
          );
        })}
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {plan.days.map((d) => {
          const isToday = d.date === today;
          const isPast = d.date < today;
          return (
            <div
              key={d.date}
              className="pm-card p-5 relative"
              style={{
                borderColor: isToday ? "var(--primary)" : "var(--border)",
                borderWidth: isToday ? 2 : 1,
                opacity: isPast && !d.completed && !isToday ? 0.7 : 1,
              }}
              data-testid={`plan-day-${d.date}`}
            >
              {d.completed && (
                <div
                  className="absolute top-3 right-3 inline-flex items-center gap-1 text-xs font-bold"
                  style={{ color: "var(--mastery)" }}
                  data-testid={`plan-day-${d.date}-complete`}
                >
                  <CheckCircle2 size={14} /> Done
                </div>
              )}
              <div className="flex items-center gap-2 text-xs mb-1 flex-wrap" style={{ color: isToday ? "var(--primary)" : "var(--text-muted)" }}>
                <span className="font-bold uppercase tracking-wide">{d.day_label}</span>
                <span>·</span>
                <span>{d.date}</span>
                {d.subject_label && (
                  <span
                    className="pm-badge text-xs"
                    style={{ background: "var(--muted)", color: "var(--text-secondary)", padding: "2px 8px" }}
                    data-testid={`plan-day-${d.date}-subject`}
                  >
                    {d.subject_label}
                  </span>
                )}
                {isToday && (
                  <span className="pm-badge ml-1" style={{ background: "var(--primary)", color: "white" }}>Today</span>
                )}
              </div>
              <h3 className="font-display text-lg font-bold leading-snug mb-1" data-testid={`plan-day-${d.date}-topic`}>
                {d.topic_id ? (
                  <Link to={reviewHref(d)} className="hover:underline" data-testid={`plan-day-${d.date}-topic-link`}>
                    {d.topic_name}
                  </Link>
                ) : d.topic_name}
              </h3>
              <p className="text-xs mb-4" style={{ color: "var(--text-muted)" }}>{d.reason}</p>
              <div className="space-y-1.5">
                {d.activities.map((a) => {
                  // Ensure flashcard link always points at the specific topic,
                  // regardless of whether the stored plan was generated before
                  // deep-linked flashcards existed.
                  const href = a.kind === "flashcard" && d.topic_id
                    ? `/app/flashcards?topic_id=${d.topic_id}`
                    : a.kind === "review" && d.topic_id
                    ? reviewHref(d)
                    : a.href;
                  return (
                    <Link
                      key={a.kind}
                      to={href}
                      className="flex items-center gap-2 p-2 rounded-lg text-sm hover:bg-[var(--muted)] transition-colors"
                      data-testid={`plan-day-${d.date}-act-${a.kind}`}
                    >
                      <span className="text-lg">{ACTIVITY_ICON[a.kind] || "✦"}</span>
                      <span className="flex-1">{a.label}</span>
                      <ArrowRight size={12} style={{ color: "var(--text-muted)" }} />
                    </Link>
                  );
                })}
              </div>
              {!d.completed && (
                <button
                  onClick={() => completeDay(d.date)}
                  className="mt-3 text-xs underline"
                  style={{ color: "var(--primary)" }}
                  data-testid={`plan-day-${d.date}-mark-done-btn`}
                >
                  Mark this day done
                </button>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-8 pm-card p-4 flex items-center gap-3">
        <Sparkles size={16} style={{ color: "var(--accent)" }} />
        <div className="text-sm" style={{ color: "var(--text-secondary)" }}>
          Plan resets automatically every Monday. Hitting the goals each day earns streak points and unlocks reward tiers.
        </div>
      </div>
      {aiTool && (
        <CoachToolModal
          open={!!aiTool}
          onClose={() => setAiTool(null)}
          type={aiTool.type}
          defaults={aiTool.defaults}
        />
      )}
    </div>
  );
}
